/**
 * Hardware Detail Component
 * Shows complete phone configuration with tabs for accounts, line keys, and expansion
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Grid,
  Button,
  Chip,
  Card,
  CardContent,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthWithCustomerOverride } from '../../hooks/useAuthWithCustomerOverride';
import { useCustomerFilter } from '../../hooks/useCustomerFilter';
import { hardwareService } from '../../services/hardwareService';
import type { Hardware, LINE_KEY_TYPES } from './types';
import ButtonConfigurator from './ButtonConfigurator';
import PhoneAccounts from './PhoneAccounts';
import VisualPhoneLayout from './VisualPhoneLayout';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
  </div>
);

const HardwareDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { member, isLoginAsCustomer } = useAuthWithCustomerOverride();
  const customerFilter = useCustomerFilter();

  // Debug logging
  console.log('[HardwareDetail] Hook values:', {
    isLoginAsCustomer,
    memberCustomerID: member?.customerID,
    memberType: member?.memberType,
    filterIsFiltered: customerFilter.isFiltered,
    filterCustomerId: customerFilter.customerId,
    sessionStorage: sessionStorage.getItem('loginAsCustomer')
  });

  const [hardware, setHardware] = useState<Hardware | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [configuratorOpen, setConfiguratorOpen] = useState(false);
  const [selectedButton, setSelectedButton] = useState<number | null>(null);
  const [isExpansion, setIsExpansion] = useState(false);

  // Helper to get customer ID for API calls - matches queues page pattern
  const getCustomerId = (): number | undefined => {
    // Priority order (matching QueuesAsCustomerNew):
    // 1. Login-as mode: use the overridden customer ID
    if (isLoginAsCustomer && member?.customerID) {
      console.log('[HardwareDetail] Using login-as customer ID:', member.customerID);
      return member.customerID;
    }
    // 2. Customer filter mode: admin managing specific customer
    if (customerFilter.isFiltered && (member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin')) {
      console.log('[HardwareDetail] Using customer filter ID:', customerFilter.customerId);
      return parseInt(customerFilter.customerId!);
    }
    // 3. Regular customer: use their own customer ID
    if (member?.customerID) {
      console.log('[HardwareDetail] Using member customer ID:', member.customerID);
      return member.customerID;
    }
    console.log('[HardwareDetail] No customer ID found!');
    return undefined;
  };

  useEffect(() => {
    if (id) {
      loadHardware();
    }
  }, [id]);

  const loadHardware = async () => {
    try {
      setLoading(true);
      setError(null);
      const customerId = getCustomerId();
      const data = await hardwareService.getById(parseInt(id!), customerId);
      setHardware(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load hardware');
    } finally {
      setLoading(false);
    }
  };

  const formatMac = (mac: string) => {
    return mac.match(/.{1,2}/g)?.join(':').toUpperCase() || mac;
  };

  const handleButtonClick = (buttonNo: number | null, expansion: boolean = false) => {
    setSelectedButton(buttonNo);
    setIsExpansion(expansion);
    setConfiguratorOpen(true);
  };

  const handleConfiguratorClose = () => {
    setConfiguratorOpen(false);
    setSelectedButton(null);
    setIsExpansion(false);
    loadHardware(); // Reload to get updated config
  };

  const getConfiguredKeys = (expansion: boolean): number[] => {
    if (expansion) {
      return hardware?.expansionKeys?.map(k => k.KeyNo) || [];
    } else {
      return hardware?.lineKeys?.map(k => k.LineKeyNo) || [];
    }
  };

  const getLineKeyConfig = (buttonNo: number) => {
    return hardware?.lineKeys?.find(lk => lk.LineKeyNo === buttonNo);
  };

  const getExpansionKeyConfig = (buttonNo: number) => {
    return hardware?.expansionKeys?.find(ek => ek.KeyNo === buttonNo);
  };

  const renderLineKeysTable = (expansion: boolean = false) => {
    const keys = expansion ? hardware?.expansionKeys || [] : hardware?.lineKeys || [];
    const accounts = hardware?.accounts || [];

    if (keys.length === 0) {
      return (
        <Alert severity="info">
          No line keys configured yet. Click "Add Line Key" to get started.
        </Alert>
      );
    }

    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Line #</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Value</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {keys.map((key) => {
              const isExpansion = 'expansion_module.key.type' in key;
              const keyNo = isExpansion ? key.KeyNo : key.LineKeyNo;
              const type = isExpansion ? key['expansion_module.key.type'] : key['linekey.type'];
              const label = isExpansion ? key['expansion_module.key.label'] : key['linekey.label'];
              const typeName = key.typeName || 'Unknown';

              // Display value - show the extension number for all types
              // Note: types are stored as strings in database ("13", "15", "16")
              let value = '';
              const typeStr = String(type); // Convert to string for comparison

              if (isExpansion) {
                switch (typeStr) {
                  case '13': // Speed Dial
                    value = key['expansion_module.key.value'] || '';
                    break;
                  case '15': // Line - show extension, not line number
                    value = key.Extension ? String(key.Extension) : '';
                    break;
                  case '16': // BLF
                    value = key.Extension ? String(key.Extension) : '';
                    break;
                }
              } else {
                switch (typeStr) {
                  case '13': // Speed Dial
                    value = key['linekey.value'] || '';
                    break;
                  case '15': // Line - show extension, not line number
                    const extNum = Number(key.Extension);
                    value = key.Extension && extNum > 0 ? String(key.Extension) : '';
                    break;
                  case '16': // BLF
                    const blfNum = Number(key.Extension);
                    value = key.Extension && blfNum > 0 ? String(key.Extension) : '';
                    break;
                }
              }

              return (
                <TableRow key={keyNo} hover>
                  <TableCell>{keyNo}</TableCell>
                  <TableCell>
                    <Chip
                      label={typeName}
                      color={type === 13 ? 'primary' : type === 15 ? 'success' : 'secondary'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{value || '-'}</TableCell>
                  <TableCell>{label || '-'}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleButtonClick(keyNo, expansion)}
                      title="Edit"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteKey(keyNo, expansion)}
                      title="Delete"
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const handleDeleteKey = async (buttonNo: number, expansion: boolean) => {
    if (!window.confirm(`Are you sure you want to delete line key ${buttonNo}?`)) {
      return;
    }

    try {
      await hardwareService.deleteLineKey(parseInt(id!), buttonNo, expansion, getCustomerId());
      await loadHardware();
    } catch (err) {
      console.error('Failed to delete line key:', err);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !hardware) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || 'Hardware not found'}</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/hardware')} sx={{ mt: 2 }}>
          Back to Hardware List
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/hardware')} sx={{ mr: 2 }}>
          <BackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" component="h1">
            {hardware.manufacturer} {hardware.model}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            MAC: {formatMac(hardware.MacAddr)} • {hardware.location || 'No location'}
          </Typography>
        </Box>
        <PhoneIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
      </Box>

      {/* Info Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">Serial Number</Typography>
            <Typography variant="h6">{hardware.SerialNum || 'N/A'}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">Type</Typography>
            <Typography variant="h6">{hardware.phoneType || 'N/A'}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">Line Keys</Typography>
            <Typography variant="h6">{hardware.linekeys}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">Admin Password</Typography>
            <Typography variant="h6" fontFamily="monospace">{hardware.password}</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label="Visual Layout" />
          <Tab label="Phone Accounts" />
          <Tab label={`Line Keys (${hardware.linekeys})`} />
          {!!hardware.extModAvail && <Tab label="Expansion Module (40)" />}
        </Tabs>

        <Divider />

        {/* Tab 0: Visual Layout */}
        <TabPanel value={tabValue} index={0}>
          <VisualPhoneLayout
            hardware={hardware}
            onButtonClick={handleButtonClick}
          />
        </TabPanel>

        {/* Tab 1: Phone Accounts */}
        <TabPanel value={tabValue} index={1}>
          <PhoneAccounts hardware={hardware} onUpdate={loadHardware} customerId={getCustomerId()} />
        </TabPanel>

        {/* Tab 2: Line Keys */}
        <TabPanel value={tabValue} index={2}>
          <Box>
            <Button
              variant="contained"
              onClick={() => handleButtonClick(null, false)}
              sx={{ mb: 2 }}
            >
              Add Line Key
            </Button>
            {renderLineKeysTable(false)}
          </Box>
        </TabPanel>

        {/* Tab 3: Expansion Module */}
        {!!hardware.extModAvail && (
          <TabPanel value={tabValue} index={3}>
            <Box>
              <Button
                variant="contained"
                onClick={() => handleButtonClick(null, true)}
                sx={{ mb: 2 }}
              >
                Add Line Key
              </Button>
              {renderLineKeysTable(true)}
            </Box>
          </TabPanel>
        )}
      </Paper>

      {/* Button Configurator Modal */}
      <ButtonConfigurator
        open={configuratorOpen}
        onClose={handleConfiguratorClose}
        hardwareId={hardware.hardwareID}
        buttonNo={selectedButton}
        isExpansion={isExpansion}
        currentConfig={
          selectedButton
            ? isExpansion
              ? getExpansionKeyConfig(selectedButton)
              : getLineKeyConfig(selectedButton)
            : undefined
        }
        accounts={hardware.accounts || []}
        totalLineKeys={hardware.linekeys}
        configuredKeys={getConfiguredKeys(isExpansion)}
        customerId={getCustomerId()}
      />
    </Box>
  );
};

export default HardwareDetail;
